import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  AuthStatus,
  CurrentUser,
  UserAddress,
} from '@/types';
import {
  registerConsumer,
  loginConsumer,
  getMeConsumer,
  loginConsumerWithGoogle,
  updateMeConsumer,
  resetConsumerPassword,
  AuthApiError,
} from './authApi';
import type {
  RegisterRequest,
  LoginRequest,
  ConsumerPayload,
  GoogleLoginRequest,
  AuthFieldErrors,
  ResetPasswordRequest,
} from './authApi';

interface UserState {
  status: AuthStatus;
  current?: CurrentUser;
  addresses: UserAddress[];
  token?: string;
  error?: string;
}

const initialState: UserState = {
  status: 'idle',
  current: undefined,
  addresses: [],
};

type AuthRejectedPayload = string | { code?: string; fieldErrors?: AuthFieldErrors };

function rejectedCode(payload: unknown): string {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload === 'object' && 'code' in payload) {
    const code = (payload as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return 'unknown';
}

function consumerToCurrentUser(c: ConsumerPayload): CurrentUser {
  const googleConnected = Boolean(c.google_connected);
  return {
    uuid: c.uuid,
    email: c.email,
    name: `${c.first_name} ${c.last_name}`.trim(),
    phone: c.phone,
    google: {
      connected: googleConnected,
      email: googleConnected ? (c.google_email ?? c.email) : undefined,
    },
  };
}

function applyLoginResult(
  state: UserState,
  payload: { token: string; consumer: ConsumerPayload },
) {
  state.status = 'authenticated';
  state.token = payload.token;
  state.current = consumerToCurrentUser(payload.consumer);
  state.error = undefined;
}

function clearDefaults(addrs: UserAddress[]) {
  for (const a of addrs) a.isDefault = false;
}

function ensureOneDefault(addrs: UserAddress[]) {
  if (addrs.length === 0) return;
  if (addrs.some(a => a.isDefault)) return;
  addrs[0].isDefault = true;
}

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

export const registerUser = createAsyncThunk(
  'user/registerUser',
  async (req: RegisterRequest, { rejectWithValue }) => {
    try {
      return await registerConsumer(req);
    } catch (err) {
      const code = err instanceof AuthApiError ? err.code : 'unknown';
      if (
        err instanceof AuthApiError
        && err.fieldErrors
        && code !== 'email_taken'
        && code !== 'recaptcha_failed'
        && code !== 'use_google_login'
        && code !== 'password_setup_required'
      ) {
        return rejectWithValue({ code, fieldErrors: err.fieldErrors });
      }
      return rejectWithValue(code);
    }
  },
);

export const loginUser = createAsyncThunk(
  'user/loginUser',
  async (req: LoginRequest, { rejectWithValue }) => {
    try {
      return await loginConsumer(req);
    } catch (err) {
      const code = err instanceof AuthApiError ? err.code : 'unknown';
      return rejectWithValue(code);
    }
  },
);

export const loginUserWithGoogle = createAsyncThunk(
  'user/loginUserWithGoogle',
  async (req: GoogleLoginRequest, { rejectWithValue }) => {
    try { return await loginConsumerWithGoogle(req); }
    catch (err) {
      const code = err instanceof AuthApiError ? err.code : 'unknown';
      return rejectWithValue(code);
    }
  },
);

/**
 * SC-778 — set the password a mailed link authorises.
 *
 * Lands in the same state as a login because the server answers with a
 * session: the client proved he reads the mailbox, which is the same evidence
 * a password would have given.
 */
export const resetPasswordUser = createAsyncThunk(
  'user/resetPasswordUser',
  async (req: ResetPasswordRequest, { rejectWithValue }) => {
    try {
      return await resetConsumerPassword(req);
    } catch (err) {
      const code = err instanceof AuthApiError ? err.code : 'unknown';
      return rejectWithValue(code);
    }
  },
);

export const updateMeUser = createAsyncThunk(
  'user/updateMeUser',
  async (
    patch: Partial<Pick<ConsumerPayload, 'phone' | 'first_name' | 'last_name'>>,
    { getState, rejectWithValue },
  ) => {
    const state = getState() as { user: UserState };
    const token = state.user.token;
    if (!token) return rejectWithValue('unauthenticated');
    try { return await updateMeConsumer(token, patch); }
    catch (err) {
      const code = err instanceof AuthApiError ? err.code : 'unknown';
      return rejectWithValue(code);
    }
  },
);

export const getMe = createAsyncThunk(
  'user/getMe',
  async (_: void, { getState, rejectWithValue }) => {
    const state = getState() as { user: UserState };
    const token = state.user.token;
    if (!token) return rejectWithValue('unauthenticated');
    try {
      return await getMeConsumer(token);
    } catch (err) {
      const code = err instanceof AuthApiError ? err.code : 'unknown';
      return rejectWithValue(code);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Legacy backward-compat reducers (kept for T4/T5/GoogleButton/existing tests)
    login(state, action: PayloadAction<{ email: string; password: string }>) {
      state.status = 'authenticated';
      state.current = { name: '', email: action.payload.email, phone: '', google: { connected: false } };
      state.error = undefined;
    },
    register(state, action: PayloadAction<{ name: string; email: string; phone: string; password: string }>) {
      const { name, email, phone } = action.payload;
      state.status = 'authenticated';
      state.current = { name, email, phone, google: { connected: false } };
      state.error = undefined;
    },
    logoutUser(state) {
      state.status = 'idle';
      state.current = undefined;
      state.token = undefined;
      state.error = undefined;
      state.addresses = [];
    },
    updateProfile(state, action: PayloadAction<Partial<CurrentUser>>) {
      if (!state.current) return;
      state.current = { ...state.current, ...action.payload };
    },
    addAddress(state, action: PayloadAction<UserAddress>) {
      const a = action.payload;
      if (a.isDefault) clearDefaults(state.addresses);
      state.addresses.push(a);
      ensureOneDefault(state.addresses);
    },
    updateAddress(state, action: PayloadAction<{ id: string; patch: Partial<UserAddress> }>) {
      const { id, patch } = action.payload;
      const idx = state.addresses.findIndex(a => a.id === id);
      if (idx < 0) return;
      if (patch.isDefault) clearDefaults(state.addresses);
      state.addresses[idx] = { ...state.addresses[idx], ...patch };
      ensureOneDefault(state.addresses);
    },
    removeAddress(state, action: PayloadAction<{ id: string }>) {
      state.addresses = state.addresses.filter(a => a.id !== action.payload.id);
      ensureOneDefault(state.addresses);
    },
    setDefaultAddress(state, action: PayloadAction<{ id: string }>) {
      const target = state.addresses.find(a => a.id === action.payload.id);
      if (!target) return;
      clearDefaults(state.addresses);
      target.isDefault = true;
    },
    _hydrateToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    // registerUser
    builder.addCase(registerUser.fulfilled, (state, action) => applyLoginResult(state, action.payload));
    builder.addCase(registerUser.rejected, (state, action) => {
      state.error = rejectedCode(action.payload as AuthRejectedPayload | undefined);
    });

    // loginUser
    builder.addCase(loginUser.fulfilled, (state, action) => applyLoginResult(state, action.payload));
    builder.addCase(loginUser.rejected, (state, action) => {
      state.error = rejectedCode(action.payload);
    });

    // resetPasswordUser
    builder.addCase(resetPasswordUser.fulfilled, (state, action) => applyLoginResult(state, action.payload));

    // loginUserWithGoogle
    builder.addCase(loginUserWithGoogle.fulfilled, (state, action) => applyLoginResult(state, action.payload));
    builder.addCase(loginUserWithGoogle.rejected, (state, action) => {
      state.error = rejectedCode(action.payload);
    });

    // updateMeUser
    builder.addCase(updateMeUser.fulfilled, (state, action) => {
      state.current = consumerToCurrentUser(action.payload.consumer);
    });
    builder.addCase(updateMeUser.rejected, (state, action) => {
      state.error = rejectedCode(action.payload);
    });

    // getMe
    builder.addCase(getMe.fulfilled, (state, action) => {
      const { consumer } = action.payload;
      state.status = 'authenticated';
      state.current = consumerToCurrentUser(consumer);
      state.error = undefined;
    });
    // Ce contrôle tourne au démarrage de l'application (`BootSessionRehydrate`),
    // sur le jeton relu du navigateur. Il déconnectait sur N'IMPORTE quel échec
    // — et comme le jeton part du stockage local avec la session, c'était
    // définitif : le téléphone sans réseau au moment du chargement, ou un hub
    // en 502 pendant dix secondes, et le client devait retaper nom, téléphone
    // et e-mail à la commande suivante, exactement ce que le compte lui promet
    // d'éviter. Seul le serveur peut invalider un jeton : un transport muet ne
    // dit rien sur sa validité, et le prochain appel authentifié rejouera la
    // question. On ne jette donc la session que sur une réponse d'autorité.
    builder.addCase(getMe.rejected, (state, action) => {
      const code = rejectedCode(action.payload);
      if (code === 'network' || code === 'server' || code === 'unknown') return;
      state.status = 'idle';
      state.current = undefined;
      state.token = undefined;
      state.error = undefined;
    });
  },
});

export const {
  login,
  register,
  logoutUser,
  updateProfile,
  addAddress,
  updateAddress,
  removeAddress,
  setDefaultAddress,
  _hydrateToken,
} = userSlice.actions;

export const _setTokenForTest = _hydrateToken;

export type { UserState };
export default userSlice.reducer;
